import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	ElementRef,
	EventEmitter,
	Inject,
	Input,
	OnChanges,
	Optional,
	Output,
	SimpleChanges,
	ViewChild
}                                        from '@angular/core';
import { CS_CHAT_CONFIG, CsChatConfig }  from './chat.config';
import { CS_CHAT_GLOSSARY, CsGlossarySource } from './cs-glossary';
import { splitChatSegments }             from './cs-chat-segments';
import { CsChatMessage }                 from './chat.message';
import { CsStickToBottomDirective }      from './cs-stick-to-bottom.directive';

/** Input-context breakdown for the latest turn (host passes it from the context.window event). */
export interface CsContextUsage {
	contextWindow: number;
	usedTokens: number;
	cacheReadTokens: number;
	components: { key: string; label: string; tokens: number }[];
}

/** One drawn slice of the context bar. */
export interface CsContextSegment { key: string; label: string; tokens: number; pct: number; }

/** One tool as sent to the model (name + description + JSON schema), with its token estimate. */
export interface CsDebugTool { name: string; description: string; parameters: string; tokens: number; }

/** Full prompt the model receives this turn, split by section — fetched on demand by the host for the
 *  advanced debug panel. Messages are rendered from the component's own {@link CsChatComponent.messages}. */
export interface CsDebugContext {
	systemPrompt: string;
	systemPromptTokens: number;
	tools: CsDebugTool[];
	toolsTokens: number;
	config: { contextWindowTokens: number; conversationModel: string; analysisModel: string; maxTokens: number };
}

/**
 * Presentational chat component. It renders a transcript and a composer, but is
 * transport-agnostic: the host supplies {@link messages}/{@link busy} and reacts
 * to {@link send}. This keeps the component reusable across the standalone
 * Cohelion.Chat.Angular app and an embedded usage inside cdp-client.
 */
@Component({
	standalone:      false,
	selector:        'cs-chat',
	templateUrl:     './chat.component.html',
	styleUrls:       ['./chat.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class CsChatComponent implements OnChanges {
	/** Conversation history rendered in the transcript. */
	@Input() messages: CsChatMessage[] = [];

	/** Disables the composer while a response is pending. */
	@Input() busy = false;

	/** Header title; falls back to the injected config, then a default. */
	@Input() title: string;

	/** When true the header title can be clicked to rename inline (host sets it once a chat is open). */
	@Input() titleEditable = false;

	/** Latest turn's context-window breakdown; drives the composer meter icon + popup. */
	@Input() contextUsage?: CsContextUsage;

	/** Whether the context breakdown popover is open. */
	ctxOpen = false;

	/** Gates the "advanced debug" affordance (a JWT claim on the host side; off unless enabled). */
	@Input() debugEnabled = false;

	/** The fetched full-prompt breakdown for the debug panel (host loads it on {@link debugRequested}). */
	@Input() debugContext?: CsDebugContext;

	/** Whether the right-hand debug drawer is open. */
	debugOpen = false;

	/** Which tool rows are expanded in the debug panel. */
	debugToolOpen = new Set<string>();

	/** Emitted when the user opens the debug panel — the host fetches and sets {@link debugContext}. */
	@Output() debugRequested = new EventEmitter<void>();

	/**
	 * Open the debug drawer (from the context popover) and ask the host to (re)load its data.
	 * No-ops unless {@link debugEnabled} — the button is already hidden without it, but this keeps a
	 * programmatic/stale call from asking the host to fetch admin-only content.
	 */
	openDebug(): void {
		if (!this.debugEnabled) { return; }
		this.ctxOpen  = false;
		this.debugOpen = true;
		this.debugRequested.emit();
	}

	closeDebug(): void { this.debugOpen = false; }

	toggleDebugTool(name: string): void {
		if (this.debugToolOpen.has(name)) this.debugToolOpen.delete(name);
		else this.debugToolOpen.add(name);
	}

	/** Used share of the context window (0–100), shown on the composer icon. */
	get ctxUsedPct(): number {
		const u = this.contextUsage;
		if (!u || u.contextWindow <= 0) return 0;
		return Math.min(100, Math.round((u.usedTokens / u.contextWindow) * 100));
	}

	/** Drawn slices: the components plus a trailing "Free space", each as a % of the window. */
	get ctxSegments(): CsContextSegment[] {
		const u = this.contextUsage;
		if (!u) return [];
		const window = Math.max(u.contextWindow, u.usedTokens, 1);
		const free   = Math.max(0, u.contextWindow - u.usedTokens);
		const parts  = [...u.components, { key: 'free', label: 'Free space', tokens: free }];
		return parts.map(p => ({ ...p, pct: (p.tokens / window) * 100 }));
	}

	/** Compact token label, e.g. 12.3k. */
	fmtTokens(n: number): string {
		if (n >= 1000) return (n / 1000).toFixed(n >= 10000 ? 0 : 1) + 'k';
		return `${n}`;
	}

	/** Rounded percentage for the legend (avoids a DecimalPipe dependency). */
	pctLabel(pct: number): number { return Math.round(pct); }

	/** Emitted when the user submits a message. */
	@Output() send = new EventEmitter<string>();

	/** Emitted when the user cancels the in-flight response (Stop button). */
	@Output() stop = new EventEmitter<void>();

	/** Emitted with the new title when the user commits an inline rename. */
	@Output() titleChange = new EventEmitter<string>();

	draft = '';

	/** Header width toggle: false = the default reading column, true = a wider column. */
	wide = false;

	/** True while the title is being edited inline; swaps the title button for an input. */
	editingTitle = false;

	/** Working copy of the title while editing. */
	titleDraft = '';

	/** The inline title input, focused/selected when an edit begins. */
	@ViewChild('titleInput') private titleInput?: ElementRef<HTMLInputElement>;

	/** The composer textarea, refocused when a turn finishes so the user can keep typing. */
	@ViewChild('composer') private composer?: ElementRef<HTMLTextAreaElement>;

	/**
	 * Debounced "Thinking…" visibility. The indicator only shows once a turn has been pending
	 * for {@link ThinkingDelayMs} — a fast reply (greeting, small talk) lands its first token
	 * before then, so no loader ever flashes for trivial prompts.
	 */
	protected showThinking = false;
	private thinkingHandle: number | null = null;
	private static readonly ThinkingDelayMs = 450;

	/**
	 * True when the host app never provided CS_CHAT_GLOSSARY — the term tooltips and the "Terms in
	 * this answer" panel are then dead, silently.
	 *
	 * This is a WIRING bug, not a configuration choice: an instance that simply has no glossary
	 * still provides the token and gets an empty term list. The distinction matters because the
	 * components inject the token with `optional: true`, so a host that forgets it renders plain
	 * prose with no error anywhere — which is exactly how this went unnoticed in the PM app. Surface
	 * it instead of failing silent.
	 */
	protected readonly glossaryMissing: boolean;

	constructor(
		@Optional() @Inject(CS_CHAT_CONFIG) public config: CsChatConfig,
		@Optional() @Inject(CS_CHAT_GLOSSARY) glossary: CsGlossarySource | null,
		private cdr: ChangeDetectorRef) {
		if (!this.title) {
			this.title = (config && config.title) || 'Cohelion Assistant';
		}

		this.glossaryMissing = !glossary;
		if (this.glossaryMissing) {
			console.warn(
				'[cs-chat] CS_CHAT_GLOSSARY is not provided — glossary tooltips and the ' +
				'"Terms in this answer" panel are disabled. Provide it from the host app ' +
				'(e.g. `{ provide: CS_CHAT_GLOSSARY, useExisting: PmGlossaryService }`).');
		}
	}

	ngOnChanges(changes: SimpleChanges): void {
		const busy = changes['busy'];
		if (!busy) { return; }

		// Entering busy: arm the debounce so the "Thinking…" indicator only appears if the
		// model is actually slow to start — instant replies never flash it.
		if (busy.currentValue === true && busy.previousValue !== true) {
			this.showThinking = false;
			if (this.thinkingHandle !== null) { clearTimeout(this.thinkingHandle); }
			this.thinkingHandle = window.setTimeout(() => {
				this.thinkingHandle = null;
				this.showThinking = true;
				this.cdr.markForCheck();
			}, CsChatComponent.ThinkingDelayMs);
		}

		// Turn ended (busy true → false): drop the indicator and return focus to the composer
		// so the user can keep typing without reaching for the mouse. Skipped if focus has since
		// moved elsewhere (e.g. the user clicked into the transcript).
		if (busy.currentValue === false && busy.previousValue === true) {
			if (this.thinkingHandle !== null) { clearTimeout(this.thinkingHandle); this.thinkingHandle = null; }
			this.showThinking = false;
			setTimeout(() => {
				const el     = this.composer?.nativeElement;
				const active = el?.ownerDocument.activeElement;
				const stolen = active && active !== document.body && active !== el;
				if (el && !el.disabled && !stolen) {
					el.focus();
				}
			});
		}
	}

	/** Mirrors the stick-to-bottom pin state; while false a "scroll to bottom" button shows. */
	pinned = true;

	/** The transcript's stick-to-bottom controller, used to force a scroll on send. */
	@ViewChild(CsStickToBottomDirective) private stickToBottom?: CsStickToBottomDirective;

	/**
	 * Jump to the newest message and re-pin. Public: hosts call it after swapping in a
	 * loaded session so an opened chat starts at the bottom. The longer force window
	 * rides out the post-load layout settles (lazy `content-visibility` rows getting
	 * their real heights, charts drawing on their first ResizeObserver tick).
	 */
	scrollToBottom(): void {
		this.stickToBottom?.pin(800);
	}

	/**
	 * The running multi-step plan, surfaced in the sticky strip above the composer while a turn
	 * is in flight. Read from the streaming assistant row's ```todos fence; null when not busy or
	 * the turn has no plan — so the strip is visible exactly while a multi-step report runs.
	 * `current` is the first not-yet-done step (what's being worked on now).
	 */
	get activePlan(): { items: { text: string; done: boolean }[]; doneCount: number; current: string | null } | null {
		if (!this.busy) { return null; }
		const last = this.messages[this.messages.length - 1];
		if (!last || last.role !== 'assistant') { return null; }
		const todos = splitChatSegments(last.content, true).find(s => s.kind === 'todos')?.todos;
		if (!todos) { return null; }
		return { ...todos, current: todos.items.find(i => !i.done)?.text ?? null };
	}

	/**
	 * Whether to show the standalone "thinking…" indicator. It appears only while
	 * busy *and before* the assistant row exists; once the assistant message
	 * starts streaming, that row's own live caret takes over.
	 */
	get awaitingResponse(): boolean {
		if (!this.busy) {
			return false;
		}
		const last = this.messages[this.messages.length - 1];
		return !last || last.role !== 'assistant';
	}

	submit(): void {
		const text = this.draft.trim();
		if (!text || this.busy) {
			return;
		}
		this.send.emit(text);
		this.draft = '';
		// Sending is an explicit action — always jump to the newest message, even
		// if the user had scrolled up to read history.
		this.stickToBottom?.pin();
	}

	/**
	 * "Explain simply" on an assistant row: re-ask the assistant to restate its previous answer in
	 * plain language. Routed through the normal `send` path so it streams and persists like any turn.
	 */
	onExplain(message: CsChatMessage): void {
		if (this.busy) { return; }
		void message; // the model has the prior turn in history; the instruction is all that's needed.
		this.send.emit(
			'Please re-explain your previous answer in plain language for someone who is not a data ' +
			'analyst. Avoid statistical jargon (or define each term in a few plain words the first time ' +
			'you use it), keep the key numbers, and lead with a one-sentence takeaway.');
		this.stickToBottom?.pin();
	}

	onKeydown(event: KeyboardEvent): void {
		if (event.key === 'Enter' && !event.shiftKey) {
			event.preventDefault();
			this.submit();
		}
	}

	cancel(): void {
		if (this.busy) {
			this.stop.emit();
		}
	}

	/** Enter inline title-edit mode (no-op unless the host marked the title editable). */
	startEditTitle(): void {
		if (!this.titleEditable) {
			return;
		}
		this.titleDraft   = this.title;
		this.editingTitle = true;
		// The input only exists after this view update; focus + select on the next tick.
		setTimeout(() => {
			this.titleInput?.nativeElement.focus();
			this.titleInput?.nativeElement.select();
		});
	}

	/** Commit the edited title (on Enter or blur). Emits only when it actually changed. */
	commitTitle(): void {
		if (!this.editingTitle) {
			return;
		}
		const next = this.titleDraft.trim();
		this.editingTitle = false;
		if (next && next !== this.title) {
			this.title = next;
			this.titleChange.emit(next);
		}
	}

	/** Abandon the edit, keeping the existing title. */
	cancelEditTitle(): void {
		this.editingTitle = false;
	}

	onTitleKeydown(event: KeyboardEvent): void {
		if (event.key === 'Enter') {
			event.preventDefault();
			this.commitTitle();
		} else if (event.key === 'Escape') {
			event.preventDefault();
			this.cancelEditTitle();
		}
	}
}
